import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

export default function ImageConfig({
  img,
  index,
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  updateImage,
  warning,
}) {
  const hasWarning = !!warning?.hasWarning;
  const exceedsCanvas =
    !!warning?.exceedsCanvas ||
    img.widthIn > CANVAS_WIDTH ||
    img.heightIn > CANVAS_HEIGHT;

  const hasNameError = !!(
    warning?.missingName ||
    warning?.duplicateName ||
    warning?.nameClashesLibrary
  );
  const hasQtyError = !!warning?.missingQuantity;
  const hasWidthError = !!(warning?.missingWidth || warning?.exceedsWidth);
  const hasHeightError = !!(warning?.missingHeight || warning?.exceedsHeight);

  const nameBorderColor = hasNameError ? "#d32f2f" : "#5A6872";
  const qtyBorderColor = hasQtyError ? "#d32f2f" : "#5A6872";
  const widthBorderColor = hasWidthError ? "#d32f2f" : "#5A6872";
  const heightBorderColor = hasHeightError ? "#d32f2f" : "#5A6872";

  return (
    <Card
      sx={{
        backgroundColor: "#f5f5f5",
        boxShadow: "none",
        border: "1px solid transparent",
        maxHeight: "auto",
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          alignContent: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            alignItems: "center",
            justifyContent: "left",
            marginTop: "1rem",
          }}
        >
          <img
            src={img.src}
            alt={`N/A`}
            style={{ maxWidth: "8%", maxHeight: "auto", objectFit: "contain" }}
          />
          <TextField
            label=""
            value={img.name || ""}
            placeholder="Name"
            variant="standard"
            onChange={(e) => updateImage(index, "name", e.target.value)}
            sx={{
              background: "#fff",
              border: `1px solid ${nameBorderColor}`,
              borderRadius: "0.5rem",
              maxWidth: "100px",
              minHeight: "3rem",

              display: "flex",
              alignItems: "center",
              "& .MuiInputBase-root": {
                height: "3rem",
                display: "flex",
                paddingLeft: "0.5rem",
                alignItems: "center",
                justifyItems: "center",
              },
              "& .MuiInputBase-input": {
                fontSize: "0.95rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiInputLabel-root": {
                top: "50%",
                left: "10%",
                transform: "translateY(-50%)",
              },
            }}
            size="medium"
            InputProps={{ disableUnderline: true }}
            InputLabelProps={{
              style: { top: "50%", transform: "translateY(-50%)" },
            }}
            required
            error={hasNameError}
            helperText={
              warning?.missingName
                ? "Required"
                : warning?.duplicateName
                  ? "Duplicate name"
                  : warning?.nameClashesLibrary
                    ? "Duplicate name in library"
                    : ""
            }
          />

          <TextField
            label="Quantity"
            type="number"
            min="1"
            variant="standard"
            value={String(img.quantity)}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) {
                updateImage(
                  index,
                  "quantity",
                  value === "" ? "" : parseInt(value)
                );
              }
            }}
            sx={{
              background: "#fff",
              border: `1px solid ${qtyBorderColor}`,
              borderRadius: "0.5rem",
              maxWidth: "80px",
              maxHeight: "3rem",

              display: "flex",
              alignItems: "center",
              "& .MuiInputBase-root": {
                height: "3rem",
                display: "flex",
                paddingLeft: "0.5rem",
                top: "-8px",
                alignItems: "center",
                justifyItems: "center",
              },
              "& .MuiInputBase-input": {
                fontSize: "0.95rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiInputLabel-root": {
                top: "-20%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                fontSize: "0.85rem",
                position: "absolute",
                textAlign: "center",
                width: "100%",
                pointerEvents: "none",
              },
            }}
            size="medium"
            InputProps={{ disableUnderline: true }}
            required
            error={hasQtyError}
            helperText={hasQtyError ? "Required" : ""}
          />

          <TextField
            label="Width (in)"
            type="number"
            variant="standard"
            inputProps={{ min: 0.1, step: 0.1 }}
            value={String(img.widthIn)}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*\.?\d*$/.test(value)) {
                updateImage(
                  index,
                  "widthIn",
                  value === "" ? "" : parseFloat(value)
                );
              }
            }}
            sx={{
              background: "#fff",
              border: `1px solid ${widthBorderColor}`,
              borderRadius: "0.5rem",
              maxWidth: "80px",
              maxHeight: "3rem",

              display: "flex",
              alignItems: "center",
              "& .MuiInputBase-root": {
                height: "3rem",
                display: "flex",
                paddingLeft: "0.5rem",
                top: "-8px",
                alignItems: "center",
                justifyItems: "center",
              },
              "& .MuiInputBase-input": {
                fontSize: "0.95rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiInputLabel-root": {
                top: "-20%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                fontSize: "0.85rem",
                position: "absolute",
                textAlign: "center",
                width: "100%",
                pointerEvents: "none",
              },
            }}
            size="medium"
            InputProps={{ disableUnderline: true }}
            required
            error={hasWidthError}
            helperText={
              warning?.missingWidth
                ? "Required"
                : warning?.exceedsWidth
                  ? `Over ${CANVAS_WIDTH}" `
                  : ""
            }
          />
          <TextField
            label="Height (in)"
            type="number"
            inputProps={{ min: 0.1, step: 0.1 }}
            variant="standard"
            value={String(img.heightIn)}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*\.?\d*$/.test(value)) {
                updateImage(
                  index,
                  "heightIn",
                  value === "" ? "" : parseFloat(value)
                );
              }
            }}
            sx={{
              background: "#fff",
              border: `1px solid ${heightBorderColor}`,
              borderRadius: "0.5rem",
              maxWidth: "80px",
              maxHeight: "3rem",

              display: "flex",
              alignItems: "center",
              "& .MuiInputBase-root": {
                height: "3rem",
                display: "flex",
                paddingLeft: "0.5rem",
                top: "-8px",
                alignItems: "center",
                justifyItems: "center",
              },
              "& .MuiInputBase-input": {
                fontSize: "0.95rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiInputLabel-root": {
                top: "-20%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                fontSize: "0.85rem",
                position: "absolute",
                textAlign: "center",
                width: "100%",
                pointerEvents: "none",
              },
            }}
            size="medium"
            InputProps={{ disableUnderline: true }}
            required
            error={hasHeightError}
            helperText={
              warning?.missingHeight
                ? "Required"
                : warning?.exceedsHeight
                  ? `Over ${CANVAS_HEIGHT}"`
                  : ""
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={img.lockAspectRatio}
                onChange={(e) =>
                  updateImage(index, "lockAspectRatio", e.target.checked)
                }
                sx={{
                  color: "#bdbdbd",
                  "&.Mui-checked": { color: "#94C940" },
                  p: 0.5,
                }}
              />
            }
            label={
              <Typography
                sx={{ fontSize: "0.95rem", color: "#333", fontWeight: 500 }}
              >
                Lock Aspect Ratio
              </Typography>
            }
            sx={{ ml: 1, mr: 2 }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
